import { Platform, ResourceType } from '../models/api/common';

export enum VerticalFilterPage {
  Inventory = 'Inventory',
  Violations = 'Violations',
  Hygiene = 'Hygiene',
  Playbooks = 'Playbooks',
}

// Single source of truth mapping for copilot and agent type
export const copilotToAgentTypeMapping = new Map<ResourceType, ResourceType>([
  [ResourceType.copilot, ResourceType.agent],
  [ResourceType.copilot_topic, ResourceType.agent_topic],
  [ResourceType.copilot_action, ResourceType.agent_action],
  [ResourceType.copilot_knowledge, ResourceType.agent_knowledge],
]);

/* 
For Table filters: 
for PowerPlatform only - send copilot type, 
for any other platform (excluding PowerPlatform) - send agent type, 
for both PowerPlatform and another platform - send both copilot and agent

For Playbooks (only 1 platform at a time):
for PowerPlatform - send copilot type
for any other platform - send agent type

No platform scope: send to BE both copilot and agent
*/
export function handleCopilotAgentNamingConversion(
  page: VerticalFilterPage,
  typeFilters: string[],
  platformFilters: string[]
): string[] | undefined {
  if (!typeFilters || typeFilters.length === 0) {
    return typeFilters;
  }

  const isPlatformScope = platformFilters?.length > 0;
  const isPlaybookPowerPlatform =
    page === VerticalFilterPage.Playbooks &&
    platformFilters?.length === 1 &&
    platformFilters.includes(Platform.powerplatform);
  const isPowerPlatformOnly = platformFilters?.length === 1 && platformFilters.includes(Platform.powerplatform);
  const isNotPowerPlatform = platformFilters?.length > 0 && !platformFilters.includes(Platform.powerplatform);

  let modifiedFilters = structuredClone(typeFilters);

  if (isPlaybookPowerPlatform) {
    return modifiedFilters;
  } else {
    if (isPlatformScope) {
      if (isPowerPlatformOnly) {
        return modifiedFilters;
      } else if (isNotPowerPlatform) {
        return _substituteCopilotWithAgentInFilters(modifiedFilters);
      } else {
        return _addBothCopilotAndAgentToFilters(modifiedFilters);
      }
    } else {
      return _addBothCopilotAndAgentToFilters(modifiedFilters);
    }
  }
}

function _substituteCopilotWithAgentInFilters(filters: string[]): string[] {
  let result = structuredClone(filters);

  for (const [copilot, agent] of copilotToAgentTypeMapping) {
    if (result.includes(copilot)) {
      result = result.filter((type) => type !== copilot);
      result.push(agent);
    }
  }

  return result;
}

// Filters out duplicated resource types for UI display
// Otherwise if both copilotAction and agentAction appear in Set then UI will show both as agentAction
export function deduplicateResourceTypesForUI(resourceTypes: Set<string>): Set<string> {
  const result = new Set<string>();

  for (const resourceType of resourceTypes) {
    let copilotType: ResourceType | undefined;
    for (const [copilot, agent] of copilotToAgentTypeMapping) {
      if (agent === resourceType) {
        copilotType = copilot;
        break;
      }
    }

    if (copilotType) {
      // If this is an agent type, add the corresponding copilot type for UI display
      result.add(copilotType);
    } else {
      // Keep the original type if it's not an agent type
      result.add(resourceType);
    }
  }

  return result;
}

function _addBothCopilotAndAgentToFilters(filters: string[]): string[] {
  let result = structuredClone(filters);

  for (const [copilot, agent] of copilotToAgentTypeMapping) {
    if (result.includes(copilot)) {
      result.push(agent);
    }
  }

  return result;
}
